import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SitesTable from './SitesTable';
import SiteFilters from './SiteFilters';
import SiteDetailModal from './SiteDetailModal';
import SiteFormModal from '../config/SiteManagement/SiteFormModal';
import { SiteDisplayInfo } from '../../types/site';
import { SiteListStatus } from '../../types/siteData';
import { useSiteData } from '../../contexts/SiteDataContext';
import * as siteService from '../../services/siteService';
import { getTodayString, addDays } from '../../utils/dateUtils';
import './sites.css';

type EditTabId = 'basic' | 'hookups' | 'pricing' | 'overage-charges' | 'fees' | 'blackouts' | 'media';

type SortKey = 'alias' | 'type_name' | 'section_name' | 'effective_rate' | 'status';

const SitesDashboard: React.FC = () => {
  // Use shared site data context
  const {
    state,
    displayedSites,
    filteredCount,
    loadSites,
    loadMoreSites,
    loadSiteDetails,
    getSiteDetails,
    isSiteDetailsLoading,
    setFilters,
    setSort,
    clearFilters,
    onSiteUpdated,
  } = useSiteData();

  const {
    storeConfig,
    storeConfigLoading,
    storeConfigLoaded,
    sitesLoading,
    sitesError,
    pagination,
    filters,
    sort,
  } = state;

  // Local UI state
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<number | null>(null);
  const [editingTab, setEditingTab] = useState<EditTabId>('basic');

  // Load sites when store config is ready
  useEffect(() => {
    if (storeConfigLoaded && !state.sitesLoaded && !sitesLoading) {
      loadSites();
    }
  }, [storeConfigLoaded, state.sitesLoaded, sitesLoading, loadSites]);

  // Get selected site and its details
  const selectedSite = useMemo(() => {
    if (!selectedSiteId) return null;
    return displayedSites.find(s => s.id === selectedSiteId) || null;
  }, [selectedSiteId, displayedSites]);

  const selectedSiteDetails = selectedSiteId ? getSiteDetails(selectedSiteId) : undefined;
  const selectedSiteDetailsLoading = selectedSiteId ? isSiteDetailsLoading(selectedSiteId) : false;

  // Handle site click - load details lazily
  const handleSiteClick = useCallback(async (site: SiteDisplayInfo) => {
    setSelectedSiteId(site.id);
    // Trigger lazy load of details (will use cache if available)
    await loadSiteDetails(site.id);
  }, [loadSiteDetails]);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setSelectedSiteId(null);
  }, []);

  // Handle edit site
  const handleEditSite = useCallback((site: SiteDisplayInfo, tab?: string) => {
    setEditingSiteId(site.id);
    setEditingTab((tab as EditTabId) || 'basic');
    setShowEditModal(true);
    setSelectedSiteId(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditingSiteId(null);
    setEditingTab('basic');
  }, []);

  // Handle edit modal save
  const handleEditModalSave = useCallback(async () => {
    setShowEditModal(false);
    if (editingSiteId) {
      onSiteUpdated(editingSiteId);
    }
    setEditingSiteId(null);
    setEditingTab('basic');
  }, [editingSiteId, onSiteUpdated]);

  // Handle quick action (maintenance)
  const handleQuickAction = useCallback(async (site: SiteDisplayInfo, action: string) => {
    if (action === 'maintenance') {
      if (!window.confirm(`Mark site ${site.alias} for maintenance for 7 days?`)) {
        return;
      }
      try {
        await siteService.createSiteBlackout({
          site_id: site.id,
          store_id: site.store_id,
          blackout_type: 'maintenance',
          title: 'Quick maintenance',
          start_date: getTodayString(),
          end_date: addDays(getTodayString(), 7),
          active: true,
        });
        onSiteUpdated(site.id);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to create maintenance blackout');
      }
    }
  }, [onSiteUpdated]);

  // Handle sort change
  const handleSort = useCallback((key: SortKey) => {
    setSort(key);
  }, [setSort]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: { search?: string; type_id?: number; section_id?: number; status?: SiteListStatus }) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Transform SiteListItem to SiteDisplayInfo for compatibility with existing components
  const transformedSites = useMemo((): SiteDisplayInfo[] => {
    return displayedSites.map(site => ({
      id: site.id,
      store_id: site.store_id,
      alias: site.alias,
      description: site.description,
      type_id: site.type_id,
      site_section_id: site.site_section_id,
      max_length: site.max_length,
      seasonal: site.seasonal,
      type_name: site.type_name,
      section_name: site.section_name,
      hookup_types: [], // Not loaded in list view anymore
      current_price: site.effectivePricing?.price,
      status: site.status,
    }));
  }, [displayedSites]);

  // Build hookup info for selected site from lazy-loaded details
  const selectedSiteHookups = useMemo(() => {
    if (!selectedSiteDetails) return [];
    return selectedSiteDetails.hookups;
  }, [selectedSiteDetails]);

  // Loading state
  if (storeConfigLoading || (sitesLoading && displayedSites.length === 0)) {
    return (
      <div className="sites-dashboard">
        <div className="sites-loading">
          <div className="loading-spinner"></div>
          <p>Loading sites...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (sitesError) {
    return (
      <div className="sites-dashboard">
        <div className="sites-error">
          <p>Error: {sitesError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sites-dashboard">
      <div className="sites-header">
        <div className="sites-title">
          <h1>Sites</h1>
          <span className="site-count">
            {displayedSites.length} of {filteredCount} sites
            {pagination.hasMore && ' (scroll for more)'}
          </span>
        </div>
      </div>

      <SiteFilters
        filters={{
          search: filters.search,
          type_id: filters.type_id,
          section_id: filters.section_id,
          status: filters.status,
        }}
        onFiltersChange={handleFiltersChange}
        siteTypes={storeConfig?.siteTypes || []}
        sections={storeConfig?.sections || []}
        hookupTypes={[]} // No hookup filter per design
      />

      {transformedSites.length === 0 && !sitesLoading ? (
        <div className="sites-empty">
          <p>No sites found matching your filters.</p>
          {(filters.search || filters.type_id || filters.section_id || filters.status) && (
            <button onClick={clearFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <SitesTable
          sites={transformedSites}
          onSiteClick={handleSiteClick}
          hasMore={pagination.hasMore}
          loadingMore={sitesLoading && displayedSites.length > 0}
          onLoadMore={loadMoreSites}
          sortKey={sort.key as SortKey}
          sortDirection={sort.direction}
          onSort={handleSort}
          allSitesLoaded={!pagination.hasMore}
        />
      )}

      {selectedSite && (
        <SiteDetailModal
          site={selectedSite}
          siteTypes={storeConfig?.siteTypes || []}
          sections={storeConfig?.sections || []}
          hookupTypes={storeConfig?.hookupTypes || []}
          siteHookups={selectedSiteHookups}
          effectivePricing={selectedSite.effectivePricing}
          storeOverageFees={storeConfig?.storeOverageFees}
          storeCharges={storeConfig?.storeCharges}
          detailsLoading={selectedSiteDetailsLoading}
          onClose={handleCloseModal}
          onEdit={() => handleEditSite(selectedSite)}
          onMarkMaintenance={() => handleQuickAction(selectedSite, 'maintenance')}
        />
      )}

      {showEditModal && editingSiteId && (
        <SiteFormModal
          siteId={editingSiteId}
          siteTypes={storeConfig?.siteTypes || []}
          sections={storeConfig?.sections || []}
          hookupTypes={storeConfig?.hookupTypes || []}
          storePricing={storeConfig?.storePricing || []}
          storeTaxes={storeConfig?.storeTaxes || []}
          storeCharges={storeConfig?.storeCharges || []}
          storeOverageFees={storeConfig?.storeOverageFees || []}
          onClose={handleEditModalClose}
          onSave={handleEditModalSave}
          initialActiveTab={editingTab}
          siteDetails={getSiteDetails(editingSiteId)}
        />
      )}
    </div>
  );
};

export default SitesDashboard;
